import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, Input, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import PrintTrtagPopup from "@user/views/popup/popup_print_trtag";

import PrintLabel, { type PrintLabelHandle } from "@user/views/print/print_label";
import PrintTicket, { type PrintTicketHandle } from "@user/views/print/print_ticket";

interface State {
  gridApi: GridApi | null,
  workStationId: string,
  printTrtagInfos: any[],
  isShowPrintTrtag: boolean
}

const defState = {
  gridApi: null,
  workStationId: "",
  printTrtagInfos: [],
  isShowPrintTrtag: false
};

interface SearchParams {
  inoutType: string,
  mngType: string,
  wh: string,
  pnLike: string,
  trNoLike: string,
  snLike: string,
  trackingIdLike: string,
  woLike: string,
  visitNoLike: string,
  workStationId: string,
  skuType: string,
  skuSize: string,
  hlLike: string,
  requesterLike: string,
  workerNameLike: string,
  erpIfCompleteStatus: string,
  workCompleteDateFrom: string,
  workCompleteDateTo: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams = {
  inoutType: "",
  mngType: "",
  wh: "",
  pnLike: "",
  trNoLike: "",
  snLike: "",
  trackingIdLike: "",
  woLike: "",
  visitNoLike: "",
  workStationId: "",
  skuType: "",
  skuSize: "",
  hlLike: "",
  requesterLike: "",
  workerNameLike: "",
  erpIfCompleteStatus: "",
  workCompleteDateFrom: dayjs().subtract(3, "day").format("YYYYMMDD") + "0000",
  workCompleteDateTo: dayjs().format("YYYYMMDD") + "2359",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const validationMsg = [
  {id: "reqInfos", type: "NotBlank", msg: "지정출고 작업을 선택해 주세요."},
  {id: "workStationId", type: "NotBlank", msg: "출고작업대를 선택해 주세요."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."}
];

function InoutHistory(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const printLabelRef = useRef<PrintLabelHandle>(null);
  const printTicketRef = useRef<PrintTicketHandle>(null);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "작업구분", field: "inoutType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutType") },
    { headerName: "상세구분", field: "inoutDetailType", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("inoutDetailType") },
    { headerName: "관리구분", field: "", width: 80, cellStyle: { textAlign: "center"},
      valueGetter: (p: any) => {
        return (p.data.workReqEtcType == "B" ? commonCode.getCodeValue("mngType", "B") : commonCode.getCodeValue("mngType", "E"));
      }
    },
    { headerName: "창고", field: "wh", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "작업일시", field: "workCompleteDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "Location", field: "locationId", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.locationId, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU Type", field: "skuType", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuType"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU Size", field: "skuSize", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuSize") },
    { headerName: "Creat Date", field: "createDate", width: 180, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Tracking ID", field: "trackingId", width: 120, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", field: "sn", headerClass: "hd-ta-left", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Work Shop", headerClass: "hd-ta-left", field: "workShop", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "W/O", headerClass: "hd-ta-left", field: "wo", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Visit No", headerClass: "hd-ta-left", field: "visitNo", width: 120, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "기번(HL)", field: "hl", width: 120, cellStyle: { textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "reqQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Work Qty", headerClass: "hd-ta-right", field: "jobQty", width: 130, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "요청자", field: "requester", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "임시작업여부", field: "", width: 100, cellStyle: { textAlign: "center"},
      valueGetter: (p: any) => {
        return (p.data.matchWorkReqType == "ERP_MATCH" ? "임시작업" : "");
      }
    },
    { headerName: "REMARK", headerClass: "hd-ta-left", field: "remark", width: 250, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업자", field: "workerName", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업대", field: "workStationId", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workStationId") },
    { headerName: "ERP I/F", field: "erpIfCompleteStatus", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("erpIfCompleteStatus") }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
    },
    selectionColumnDef: {
      resizable: true
    }
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!isActiveTab || state.isShowPrintTrtag) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [isActiveTab, state.isShowPrintTrtag]);
  // 끝: 바코드 스캔 기능 적용

  // 작업대ID 기본값 지정
  useEffect(() => {
    const workStationId = useSettingsStore.getState().getDefWorkStationId(location.path) || (commonCode.workStationId as any[])?.[0]?.code || "";
    setState(prev => ({ ...prev, workStationId}));
  }, [location.path]);

  // 변경 작업대ID 저장
  useEffect(() => {
    if (state.workStationId) {
      useSettingsStore.getState().setLocalSetting("workStationId", location.path, state.workStationId);
    }
  }, [state.workStationId]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/report/inout_history.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/report/inout_history", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/report/inout_history", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/report/inout_history", {});
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/report/inout_history.download?mode=search", searchParams, {
        "fileName": "입출고실적_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }
 
  async function printTrtag() {
    openPopupPrintTrtag();
  }

  async function printLabel() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("라벨 출력 작업을 선택해 주세요.");
      return;
    }

    await printLabelRef.current?.print(selectedInfos);
  }

  async function printTicket() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("티켓 출력 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => !item.ticketFileName);
    if (infos.length > 0) {
      await kutil.alert("티켓 파일이 있는 작업만 출력할 수 있습니다.");
      return;
    }

    await printTicketRef.current?.print({
      infos: selectedInfos,
      ticketType: "1",
    });
  }

  async function reqSkuPick() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("지정출고 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => item.outPossYn == "N");
    if (infos.length > 0) {
      await kutil.alert("작업중인 SKU는 지정출고할 수 없습니다.");
      return;
    }

    if (!state.workStationId) {
      kutil.alert("출고작업대를 선택해 주세요.");
      return;
    }

    for (const info of selectedInfos) {
      info.workStationId = state.workStationId;
    }

    if (await kutil.confirm("지정출고하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/report/inout_history.proc?mode=reqSkuPick", selectedInfos, {
        validationMsg: validationMsg
      });
      await kutil.alert("지정출고하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function openPopupPrintTrtag() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length === 0) {
      await kutil.alert("TR Tag 출력 작업을 선택해 주세요.");
      return;
    }

    const infos = (selectedInfos as any[]).filter(item => !item.trFileName);
    if (infos.length > 0) {
      await kutil.alert("TR Tag 파일이 있는 작업만 출력할 수 있습니다.");
      return;
    }

    setState(prev => ({ ...prev, isShowPrintTrtag: true, printTrtagInfos: selectedInfos}));
  }

  async function closePopupPrintTrtag(_data: any) {
    setState(prev => ({ ...prev, isShowPrintTrtag: false }));
  }

  function selectTrackingId(trackingId: string) {
    const api = state.gridApi;
    if (!api) return;

    let found = false;
    api.forEachNode((node) => {
      if (node.data?.trackingId === trackingId) {
        node.setSelected(true);
        api.ensureNodeVisible(node, "middle");
        found = true;
      }
    });

    if (!found) {
      kutil.alert("일치하는 Tracking ID가 없습니다.\r\n" + trackingId);
    }
  }

  async function onBarcodeScan(code: string) {
    const [prefix, ...rest] = code.split(":");
    const value = rest.join(":").trim();

    if (rest.length > 0) {
      switch (prefix) {
        case "WS": {
          if (!value) return;
          const exists = (commonCode.workStationId as any[]).some(c => c.code === value);
          if (!exists) {
            kutil.alert("일치하는 작업대가 없습니다.\r\n" + code);
            return;
          }
          setState(prev => ({ ...prev, workStationId: value }));
          return;
        }
        default:
          return;
      }
    }

    selectTrackingId(code);
  }

  return (
    <>
      {/* contents */}
      <div className="contents list-grid-top">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>입출고 실적</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
            { auth.download &&
            <Button variant="outlined" onClick={printLabel}>라벨 출력</Button>
            }
            { auth.download &&
            <Button variant="outlined" onClick={printTicket}>티켓 출력</Button>
            }
            { auth.download &&
            <Button variant="outlined" onClick={printTrtag}>TR Tag 출력</Button>
            }
            { auth.write &&
            <Button variant="contained" onClick={reqSkuPick}>지정출고</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>작업구분</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.inoutType}
                onChange={(e) => {doSearch(e, {inoutType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.inoutType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>관리구분</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.mngType}
                onChange={(e) => {doSearch(e, {mngType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.mngType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>창고</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.wh}
                onChange={(e) => {doSearch(e, {wh: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.wh as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>P/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.pnLike}
              onChange={(e) => {updateSearchParams({ pnLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="P/N"
            />
          </div>
          <div>
            <p>TR No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.trNoLike}
              onChange={(e) => {updateSearchParams({ trNoLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="TR No"
            />
          </div>
          <div>
            <p>S/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.snLike}
              onChange={(e) => {updateSearchParams({ snLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="S/N"
            />
          </div>
          <div>
            <p>Tracking ID</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.trackingIdLike}
              onChange={(e) => {updateSearchParams({ trackingIdLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="Tracking ID"
            />
          </div>
          <div>
            <p>W/O</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.woLike}
              onChange={(e) => {updateSearchParams({ woLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="W/O"
            />
          </div>
          <div>
            <p>Visit No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.visitNoLike}
              onChange={(e) => {updateSearchParams({ visitNoLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="Visit No"
            />
          </div>
          <div>
            <p>작업대</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.workStationId}
                onChange={(e) => {doSearch(e, {workStationId: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.workStationId as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>SKU Type</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.skuType}
                onChange={(e) => {doSearch(e, {skuType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.skuType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>SKU Size</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.skuSize}
                onChange={(e) => {doSearch(e, {skuSize: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.skuSize as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>기번(HL)</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.hlLike}
              onChange={(e) => {updateSearchParams({ hlLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="기번(HL)"
            />
          </div>
          <div>
            <p>요청자</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.requesterLike}
              onChange={(e) => {updateSearchParams({ requesterLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="요청자"
            />
          </div>
          <div>
            <p>작업자</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.workerNameLike}
              onChange={(e) => {updateSearchParams({ workerNameLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="작업자"
            />
          </div>
          <div>
            <p>ERP I/F</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.erpIfCompleteStatus}
                onChange={(e) => {doSearch(e, {erpIfCompleteStatus: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.erpIfCompleteStatus as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="date-time-range">
            <p>작업일시</p>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  ampm={false}
                  timeSteps={{ hours: 1, minutes: 1 }}
                  sx={{ width: 200 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD HH:mm"
                  value={dayjs(searchParams.workCompleteDateFrom)}
                  onChange={(e) => doSearch(e, { workCompleteDateFrom: e != null ? dayjs(e).format("YYYYMMDDHHmm") : "" })}
                  maxDateTime={dayjs(searchParams.workCompleteDateTo)}
                />
                <span>-</span>
                <DateTimePicker
                  ampm={false}
                  timeSteps={{ hours: 1, minutes: 1 }}
                  sx={{ width: 200 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD HH:mm"
                  value={dayjs(searchParams.workCompleteDateTo)}
                  onChange={(e) => doSearch(e, { workCompleteDateTo: e != null ? dayjs(e).format("YYYYMMDDHHmm") : "" })}
                  minDateTime={dayjs(searchParams.workCompleteDateFrom)}
                />
              </LocalizationProvider>
            </div>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 상단 */}
        <div className="grid-top-area">
          <div className="workstation">
            <p>출고작업대</p>
            <FormControl size="small">
              <Select
                value={state.workStationId}
                onChange={(e) => {setState(prev => ({ ...prev, workStationId: e.target.value }))}}
                displayEmpty
              >
              {(commonCode.workStationId as any[]).map((code) => (
                <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
              ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {/*// 그리드 상단 */}
        {/* 입출고 실적 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {state.isShowPrintTrtag && <PrintTrtagPopup isShow={state.isShowPrintTrtag} onClose={closePopupPrintTrtag} id={props.id} data={{infos: state.printTrtagInfos}} />}
        <PrintLabel ref={printLabelRef} />
        <PrintTicket ref={printTicketRef} />
        {/*// 입출고 실적 정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(InoutHistory);
